import React from 'react'
import { motion } from 'framer-motion'
import { Target, Eye, Sparkles } from 'lucide-react'
import { Section, Card } from '../components/ui'
import { cn } from '../utils'

const values = [
  {
    icon: Target,
    title: 'Mission-Driven',
    description: 'Every initiative is guided by our commitment to Africa\'s sustainable development and the empowerment of its people.',
  },
  {
    icon: Eye,
    title: 'Evidence-Based',
    description: 'Our work is grounded in rigorous research, data analysis, and real-world validation of outcomes.',
  },
  {
    icon: Sparkles,
    title: 'Innovation-Focused',
    description: 'We embrace emerging technologies and novel approaches to solve age-old challenges in new ways.',
  },
]

const timeline = [
  {
    year: '2023',
    title: 'Foundation',
    description: 'MATNO was established with a focus on African development.',
  },
  {
    year: '2024',
    title: 'Research Initiative',
    description: 'Launched our first research programs on AI and human capital development.',
  },
  {
    year: '2025',
    title: 'Expansion',
    description: 'Growing partnerships and expanding research scope across the continent.',
  },
]

const About: React.FC = () => {
  return (
    <>
      {/* Hero */}
      <Section background="muted" padding="xl" className="pt-32 md:pt-40">
        <motion.div
          className="max-w-3xl mx-auto text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-sm font-medium text-primary-500 dark:text-primary-400 uppercase tracking-wider mb-4">
            About Us
          </p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 dark:text-white mb-6">
            Unlocking Africa's Potential
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
            MATNO is a Think & Do Tank dedicated to harnessing Africa's greatest
            asset: its people. Through research, innovation, and strategic partnerships,
            we're working to prepare the continent for the future of work.
          </p>
        </motion.div>
      </Section>

      {/* Vision & Mission */}
      <Section background="default" padding="lg">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Card variant="ghost" padding="lg">
              <p className="text-sm font-semibold text-primary-500 dark:text-primary-400 uppercase tracking-wider mb-2">
                Our Vision
              </p>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
                A Thriving Africa
              </h2>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                We envision a Africa where every individual has the opportunity
                to contribute to and benefit from the region's economic growth.
                A future where technology and human ingenuity combine to solve
                the continent's most pressing challenges.
              </p>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Card variant="ghost" padding="lg">
              <p className="text-sm font-semibold text-primary-500 dark:text-primary-400 uppercase tracking-wider mb-2">
                Our Mission
              </p>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
                Research to Action
              </h2>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                To bridge the gap between academic research and real-world impact
                by producing actionable insights, developing innovative solutions,
                and building partnerships that drive sustainable development
                across Africa.
              </p>
            </Card>
          </motion.div>
        </div>
      </Section>

      {/* Values */}
      <Section background="muted" padding="lg">
        <Section.Header
          subtitle="Our Values"
          title="What Guides Us"
          align="center"
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {values.map((value, index) => {
            const Icon = value.icon
            return (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card variant="default" padding="lg" className="h-full text-center">
                  <div className={cn(
                    'w-12 h-12 mx-auto rounded-xl flex items-center justify-center mb-4',
                    'bg-primary-50 dark:bg-primary-900/30',
                    'text-primary-500 dark:text-primary-400'
                  )}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                    {value.description}
                  </p>
                </Card>
              </motion.div>
            )
          })}
        </div>
      </Section>

      {/* Timeline */}
      <Section background="default" padding="lg">
        <Section.Header
          subtitle="Our Journey"
          title="Building Momentum"
          align="center"
        />

        <div className="max-w-2xl mx-auto">
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-8 top-0 bottom-0 w-px bg-gray-200 dark:bg-gray-800" />

            {timeline.map((item, index) => (
              <motion.div
                key={item.year}
                className="relative flex gap-6 pb-10 last:pb-0"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                {/* Year dot */}
                <div className={cn(
                  'w-16 h-16 rounded-full flex-shrink-0 flex items-center justify-center',
                  'bg-primary-50 dark:bg-primary-900/30',
                  'border-4 border-white dark:border-gray-950',
                  'text-primary-600 dark:text-primary-400 font-bold text-sm',
                  'z-10'
                )}>
                  {item.year}
                </div>

                {/* Content */}
                <div className="pt-3">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {item.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>
    </>
  )
}

export default About
